

































































































































































































import CoThumbnail from '@/components/Molecules/co-thumbnail/CoThumbnail.vue';
import EventBus from '@/eventBus';
import axios from 'axios';
import { get, pick, set, uniqBy, isEqual, debounce } from 'lodash';
import CoMultiSelect from '@/components/Molecules/co-multiselect/CoMultiSelect.vue';
import CoAlert from '@/components/Molecules/co-alert/CoAlert.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import { normalizeAttachmentFile } from '@/utils/attachment.ts';
import CoModal from '../co-modal/CoModal.vue';
import CoEditor from '../../Molecules/co-editor/CoEditor.vue';
import CoButton from '../../Atoms/co-button/CoButton.vue';
import CoIcon from '../../Atoms/co-icon/CoIcon.vue';

import CoLinkPreview from '../co-link-preview/CoLinkPreview.vue';
import CoContentPreview from '../co-content-preview/CoContentPreview.vue';
import CoFile from '../CoFile/CoFile.vue';
import { DestinationSelection, PageOption, PrefilledContent, PostContent } from './types.ts';
import {
    getDestinationConfig,
    getFeedDisplayForSelection,
    getInitialFeedSelection,
    isEventItem,
    isPagePrivateOrUnpublished,
    mapFeaturedContentType,
    mapPageToSelectItem,
} from './usePostDestination.ts';
import { submitPostRequest, uploadImages } from './postEditorService.ts';

const POST_EDITOR_DRAFT_KEY_PREFIX = 'coapp:post-editor:draft';
const POST_EDITOR_DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

export default {
    name: 'CoPostEditor',
    components: {
        CoModal,
        CoEditor,
        CoButton,
        CoThumbnail,
        CoIcon,
        CoLinkPreview,
        CoContentPreview,
        CoFile,
        CoMultiSelect,
        CoAlert,
        CoHeadline,
    },
    props: {
        user: {
            type: Object,
            default: () => ({}),
        },
        parent: {
            type: Object,
            default: () => ({}),
        },
        parentType: {
            type: String,
            validator: (value) => ['project', 'post', 'event', 'job', 'page'].includes(value),
            default: null,
        },
        showOnMount: {
            type: Boolean,
            default: false,
        },
        prefilledContent: {
            type: Object,
            default: null,
        },
        mobile: {
            type: Boolean,
            default: false,
        },
        editContent: {
            type: Object,
            default: () => ({}),
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        disablePoll: {
            type: Boolean,
            default: true,
        },
        disableAttachments: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: '',
        },
    },
    data(): {
        content: PostContent;
        loading: boolean;
        callBack: Function | null;
        feedOption: any;
        baseMaxDestinationSelections: number;
        pageOptions: PageOption[];
        lockedPageItem: PageOption | null;
        destinationLocked: boolean;
        feedLocked: boolean;
        destinationSelection: DestinationSelection;
        runtimePrefilledContent: PrefilledContent | null;
        runtimeEditMode: boolean;
        activeDraftKey: string | null;
        modalVisible: boolean;
        restoredDraftContent: PostContent | null;
        persistDraftDebounced: any;
    } {
        const feedOption = {
            Name: 'Feed',
            Value: 'feed',
            Disabled: false,
            BorderBelow: true,
        };
        return {
            content: {},
            loading: false,
            callBack: null, // function to call after successful post creation or update
            feedOption,
            baseMaxDestinationSelections: 2,
            pageOptions: [],
            lockedPageItem: null,
            destinationLocked: false,
            feedLocked: false,
            destinationSelection: {
                page: null,
                event: null,
                feed: true,
            },
            runtimePrefilledContent: this.prefilledContent as PrefilledContent,
            runtimeEditMode: this.editMode,
            activeDraftKey: null,
            modalVisible: false,
            restoredDraftContent: null,
            persistDraftDebounced: debounce(function () {
                this.persistDraft();
            }, 300),
        };
    },
    watch: {
        prefilledContent: {
            handler(newPrefilledContent) {
                this.runtimePrefilledContent = newPrefilledContent as PrefilledContent;
                this.setAttachmentsFromPrefilledContent(newPrefilledContent);
                this.initializeDestinationState(newPrefilledContent);
            },
            immediate: true,
        },
        editMode: {
            handler(newEditMode) {
                this.runtimeEditMode = !!newEditMode;
            },
            immediate: true,
        },
        content: {
            handler() {
                this.persistDraftDebounced();
            },
            deep: true,
        },
    },
    computed: {
        isMobile() {
            return this.$isMobile || this.mobile;
        },
        hasContent(): boolean {
            const hasText = this.hasDraftableTextContent();
            const hasPoll = !!(this.content?.IsPoll && this.isValidPoll(this.content?.Poll));
            const hasFiles = Array.isArray(this.content?.Files) && this.content.Files.length > 0;
            const hasAttachments = Array.isArray(this.content?.Attachments) && this.content.Attachments.length > 0;
            return hasText || hasPoll || hasFiles || hasAttachments;
        },
        hasDraftableContent(): boolean {
            // Drafts should persist in-progress poll edits, even before poll is valid for publishing.
            // Note: Files are intentionally excluded here because they are not persisted/restored by the draft system.
            const hasText = this.hasDraftableTextContent();
            const hasAttachments = Array.isArray(this.content?.Attachments) && this.content.Attachments.length > 0;
            const hasPollDraft = this.hasAnyPollDraft(this.content?.Poll);
            return hasText || hasAttachments || hasPollDraft;
        },
        pollDisabled(): boolean {
            return this.runtimeEditMode || this.disablePoll;
        },
        destinationConfig() {
            return getDestinationConfig(
                this.runtimePrefilledContent as PrefilledContent,
                this.$t ? this.$t.bind(this) : undefined
            );
        },
        shouldUseImplicitDestinationRules(): boolean {
            return !this.destinationConfig.hasDefinedDestination;
        },
        resolvedParentType(): number | null {
            if (this.selectedEvent) return 1;
            if (this.isPagesEnabled && this.selectedPage) return 2;
            return this.destinationConfig.parentType;
        },
        // PagesOff prevents posts from being targeted to page channels.
        isPagesEnabled(): boolean {
            return !this.$store?.state?.space?.PagesOff;
        },
        feedSelectOption() {
            return {
                ...this.feedOption,
                Name: this.$t ? this.$t('labels.feed') : this.feedOption.Name,
            };
        },
        eventDestinationOption() {
            return this.destinationConfig.event;
        },
        isEventSelectorVisible(): boolean {
            return !!this.eventDestinationOption;
        },
        destinationStaticItems() {
            const items = [this.feedSelectOption];
            const eventOption = this.selectedEvent || this.eventDestinationOption;
            if (eventOption) {
                items.push(eventOption);
            }
            return items;
        },
        destinationSelectorDisabled(): boolean {
            if (this.runtimeEditMode) return true;
            return this.destinationConfig.hasDefinedDestination && this.destinationLocked && this.feedLocked;
        },
        destinationValue() {
            const output = [];
            if (this.isFeedSelected) {
                output.push(this.feedSelectOption);
            }
            if (this.selectedEvent) {
                output.push(this.selectedEvent);
            }
            if (this.selectedPage) {
                output.push(this.selectedPage);
            }
            return output;
        },
        selectedPage() {
            if (this.destinationSelection.page) return this.destinationSelection.page;
            if (this.destinationLocked && this.lockedPageItem) return this.lockedPageItem;
            return null;
        },
        selectedEvent() {
            if (this.destinationSelection.event) return this.destinationSelection.event;
            if (this.destinationLocked && this.destinationConfig.event) return this.destinationConfig.event;
            return null;
        },
        isFeedSelected(): boolean {
            return !!this.destinationSelection.feed;
        },
        resolvedMaxDestinationSelections(): number {
            const selectedPage = this.selectedPage;
            if (this.shouldUseImplicitDestinationRules && selectedPage && isPagePrivateOrUnpublished(selectedPage)) {
                return 1;
            }
            return this.baseMaxDestinationSelections;
        },
        lockPrefilledPage(): boolean {
            return this.destinationLocked && !!this.selectedPage;
        },
        destinationSearchable(): boolean {
            if (!this.isPagesEnabled || this.runtimeEditMode || this.disablePageSearch || this.feedLocked) return false;
            return !this.destinationConfig.hasDefinedDestination;
        },
        disablePageSearch(): boolean {
            return this.destinationConfig.hasDefinedDestination || !!this.destinationConfig.disablePageSearch;
        },
        availablePageOptions() {
            if (!this.isPagesEnabled) {
                return [];
            }
            if (this.selectedEvent || this.eventDestinationOption) {
                return [];
            }
            if (this.destinationConfig.hasDefinedDestination) {
                return this.selectedPage ? [this.selectedPage] : [];
            }
            return this.pageOptions;
        },
        showDestinationSelector(): boolean {
            return true;
        },
        destinationContext() {
            const selectedPage = this.selectedPage;
            const selectedEvent = this.selectedEvent;
            const feedSelected = this.isFeedSelected;
            let feedDisplay = this.destinationConfig.feedDisplay;

            if (selectedEvent) {
                feedDisplay = getFeedDisplayForSelection(selectedEvent, feedSelected, false);
            } else if (selectedPage) {
                feedDisplay = getFeedDisplayForSelection(
                    selectedPage,
                    feedSelected,
                    this.shouldUseImplicitDestinationRules
                );
            } else if (feedDisplay === null || feedDisplay === undefined) {
                feedDisplay = getFeedDisplayForSelection(null, feedSelected, false);
            }

            return {
                page: selectedPage,
                event: selectedEvent,
                feedSelected,
                feedDisplay,
                usingPrefilledParent:
                    this.destinationConfig.hasDefinedDestination &&
                    (!!selectedPage || !!selectedEvent || this.destinationConfig.parentID !== null),
                parentType: selectedEvent ? 1 : selectedPage ? 2 : this.destinationConfig.parentType,
                parentLocked: this.destinationLocked,
            };
        },
        postVisibilityMessage(): string | null {
            const context = this.destinationContext;
            const page = context.page;
            const event = context.event;
            if (event) {
                if (context.feedSelected) {
                    return this.$t('messages.postVisibility.feedAndEvent');
                }
                return this.$t('messages.postVisibility.eventAudience');
            }
            if (context.usingPrefilledParent && context.parentType === 1) {
                if (context.feedDisplay === 2 || context.feedSelected) {
                    return this.$t('messages.postVisibility.feedAndEvent');
                }
                return this.$t('messages.postVisibility.eventAudience');
            }

            if (!page && context.feedSelected) {
                return this.$t('messages.postVisibility.feedOnly');
            }

            if (!page) return null;
            const pageIsPrivate = this.shouldUseImplicitDestinationRules && isPagePrivateOrUnpublished(page);
            if (pageIsPrivate && context.usingPrefilledParent) {
                return this.$t('messages.postVisibility.privatePageOnly');
            }

            if (pageIsPrivate && !context.usingPrefilledParent) {
                return this.$t('messages.postVisibility.privatePageOnlyWithName', {
                    pageName: this.getPageName(page),
                });
            }
            if (!context.feedSelected) {
                return pageIsPrivate
                    ? this.$t('messages.postVisibility.privatePageOnly')
                    : this.$t('messages.postVisibility.publicPageOnly');
            }

            if (context.usingPrefilledParent && context.parentLocked) {
                return this.$t('messages.postVisibility.publicPageFeedAndParentLocked');
            }

            return this.$t('messages.postVisibility.publicPageFeedAndParent', {
                pageName: this.getPageName(page),
            });
        },
        effectivePrefilledContent(): PrefilledContent | null {
            if (!this.restoredDraftContent) return this.runtimePrefilledContent as PrefilledContent;
            return {
                ...(this.runtimePrefilledContent || {}),
                ...this.restoredDraftContent,
            };
        },

        shouldShowFeaturedSection(): boolean {
            if (
                this.featuredContent ||
                this.linkToDisplay ||
                (this.content.Attachments && this.content.Attachments.length > 0)
            ) {
                return true;
            }
            return false;
        },
        hasImages(): boolean {
            return Array.isArray(this.content?.Files) && this.content.Files.length > 0;
        },
        // returns either the featured content from the prefilled content or links found that is suitable for featuring based on domain
        featuredContent() {
            // check for featured content in prefilled content first
            const prefilledContent = this.runtimePrefilledContent as PrefilledContent | null;
            const contentId = prefilledContent?.FeatureContentID || null;
            const contentType = prefilledContent?.FeaturedContentType || null;
            const normalizedType = mapFeaturedContentType(contentType);
            if (contentId && normalizedType) {
                return {
                    ID: contentId,
                    Type: normalizedType,
                };
            }

            // if there is no prefilled featured content, and the post has images, do not show link preview as featured content it will be shown as a link preview instead  and return null for featured content to avoid showing duplicate link preview in the rich media area
            if (this.hasImages) return null;

            // if no featured content, check for links in the post content that are suitable for featuring
            let links = [];
            if (this.content && this.content.Html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(this.content.Html, 'text/html');
                links = Array.from(doc.getElementsByTagName('a')).map((link) => link.href);
            }
            links = uniqBy(links, (link) => {
                try {
                    const url = new URL(link);
                    return url.href;
                } catch (e) {
                    return link;
                }
            });

            // Direct store access keeps Vue tracking the space domain dependency reactively.
            const spaceDomainName = this.$store?.state?.space?.DomainName;
            links = links.filter((link) => {
                try {
                    const url = new URL(link);
                    return url.host && url.host === spaceDomainName;
                } catch (e) {
                    return false;
                }
            });

            if (links.length > 0) {
                return {
                    Slug: links[0],
                    Link: links[0],
                };
            }
            return null;
        },
        //  returns first link found in the post content that are not suitable for featuring based on domain
        linkToDisplay() {
            if (this.featuredContent && this.hasImages) return null;

            let links = [];
            if (this.content && this.content.Html) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(this.content.Html, 'text/html');
                links = Array.from(doc.getElementsByTagName('a')).map((link) => link.href);
            }
            links = uniqBy(links, (link) => {
                try {
                    const url = new URL(link);
                    return url.href;
                } catch (e) {
                    return link;
                }
            });

            const featured = this.featuredContent ? this.featuredContent.Link : null;
            links = links.filter((link) => {
                if (featured && link === featured) {
                    return false;
                }
                return true;
            });
            return links.length > 0 ? links[0] : null;
        },
    },
    mounted() {
        if (this.showOnMount) this.show();
        this.getMyProjectsForSelector();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        if (!this.shouldWarnBeforeUnload()) {
            next();
            return;
        }
        this.persistDraft();
        this.notifyDraftSaved();
        next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        this.persistDraftDebounced.cancel();
    },
    methods: {
        isEventItem,
        setAttachmentsFromPrefilledContent(prefilledContent: PrefilledContent | null) {
            if (!this.content || typeof this.content !== 'object') {
                this.content = {};
            }
            const attachments = Array.isArray(prefilledContent?.Attachments) ? prefilledContent.Attachments : [];
            this.normalizeEditorAttachments(attachments);
        },
        normalizeEditorAttachments(attachments: any[] | null | undefined) {
            if (!this.content || typeof this.content !== 'object') {
                this.content = {};
            }
            const normalized = (attachments || []).map((attachment) => normalizeAttachmentFile(attachment));
            if (!isEqual(this.content.Attachments, normalized)) {
                this.$set(this.content, 'Attachments', normalized);
            }
        },

        launch(prefilledContent: PrefilledContent | null = null, editMode = false) {
            this.show(prefilledContent, editMode);
        },
        show(prefilledContent: PrefilledContent | null = null, editMode = false, callBack: Function = null) {
            this.callBack = callBack;
            this.runtimePrefilledContent = prefilledContent;
            this.runtimeEditMode = editMode;
            this.restoredDraftContent = null;
            this.initializeDestinationState(prefilledContent);
            this.modalVisible = true;
            this.initializeDraftKey(prefilledContent, editMode);
            this.hydrateDraftFromStorage();
            this.$refs.postmodal.show();
        },
        discard() {
            if (this.hasDraftableContent) {
                this.persistDraft();
                this.notifyDraftSaved();
            }
            // reset content and hide modal
            this.content = {};
            this.lockedPageItem = null;
            this.destinationLocked = false;
            this.feedLocked = false;
            this.resetDestinationSelection();
            this.callBack = null;
            this.loading = false;
            this.modalVisible = false;
            this.restoredDraftContent = null;
            this.$refs.postmodal.hide();
            this.$emit('discard');
        },
        async postSubmit(isUpdate = false) {
            this.loading = true;
            try {
                const parentPayload = this.resolveParentPayload();
                // upload images
                const imageUpload =
                    this.content.Files && this.content.Files.length > 0 ? await uploadImages(this.content.Files) : null;
                const imageURLs = imageUpload ? imageUpload.map((response) => response.data.url) : null;

                const postText = this.content.IsPoll && this.content.Poll ? '' : this.content.Html || '';
                const attachments =
                    this.content.Attachments && this.content.Attachments.length > 0
                        ? this.content.Attachments.map((attachment) =>
                              this.mapAttachmentFromMediaServiceToPostStruct(attachment)
                          )
                        : [];

                const payload = {
                    ...pick(this.runtimePrefilledContent as PrefilledContent, [
                        'ID',
                        'AuthorID',
                        'CreatedAt',
                        'Slug',
                        'Type',
                        'FeatureContentID',
                        'FeaturedContentType',
                    ]),
                    ParentID: parentPayload.ParentID,
                    ParentType: parentPayload.ParentType,
                    FeedDisplay: parentPayload.FeedDisplay,
                    UpdatedAt: isUpdate ? Math.round(new Date().getTime() / 1000).toString() : null,
                    Text: postText,
                    Images: imageURLs,
                    Version: '1',
                    PollQuestion: this.content.IsPoll && this.content.Poll ? this.content.Poll.PollQuestion : null,
                    PollOptions: this.content.IsPoll && this.content.Poll ? this.content.Poll.PollOptions : null,
                    PollMultiSelect:
                        this.content.IsPoll && this.content.Poll ? this.content.Poll.PollMultiSelect : null,
                    PollExpiresAt: this.content.IsPoll && this.content.Poll ? this.content.Poll.PollExpiresAt : null,
                    Variant: this.content.IsPoll && this.content.Poll ? 2 : 1,
                    Attachments: attachments,
                };

                const response = await submitPostRequest(isUpdate, payload);
                const redirectPage = this.selectedPageForRedirect();
                const createdPost = get(response, 'data', {});
                const createdPostID = get(createdPost, 'ID', get(createdPost, 'id', null));
                const redirectPostId = createdPostID;
                EventBus.$emit('INFO', {
                    Message: this.$t('labels.postpublished'),
                    Details: '',
                });
                if (this.callBack && typeof this.callBack === 'function') {
                    this.callBack(response?.data || {});
                }
                this.content = {};
                this.clearDraft();
                this.discard();

                const obj = {
                    object: {
                        ...createdPost,
                        // Event feeds use object.ID to reconcile posts arriving from SSE.
                        ID: createdPostID,
                        Author: this.user || {},
                        Type: 'post',
                    },
                    type: 'post',
                    score: get(createdPost, 'CreatedAt', null),
                    createdBy: get(createdPost, 'AuthorID', null),
                    ID: createdPostID,
                };
                set(
                    obj,
                    'object.Images',
                    get(obj, 'object.Images', []).map((image) => ({
                        ImageURL: image,
                    }))
                );

                if (isUpdate) {
                    this.$emit('updated', obj);
                } else {
                    this.$emit('created', obj);
                }

                // if parent is event, redirect to event page + postpin in queryparam
                if (parentPayload && parentPayload.ParentType === 1 && parentPayload.FeedDisplay !== 2) {
                    return;
                }
                this.redirectToPageIfNeeded(redirectPage, redirectPostId);
            } catch (error) {
                // error handling for image uploads
                const errorMsg = {
                    Message: 'Could not upload one or more images, please try again or report a bug.',
                    Details: (error as any)?.response?.data,
                };
                if ((error as any)?.response?.data?.message === 'Request Entity Too Large') {
                    errorMsg.Message = 'The file you tried to upload is too large. Max 10 MB.';
                }
                EventBus.$emit('ERROR', errorMsg);
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        uploading() {
            this.loading = true;
        },
        removeFile(index: number) {
            if (this.content.Attachments && this.content.Attachments.length > index) {
                this.$refs.posteditor.removeAttachment(this.content.Attachments[index]);
            }
        },
        fileUploaded(data: any, index: number) {
            this.loading = false;
            if (!data || !data.url) return;
            const attachments = this.content.Attachments || [];
            if (!attachments[index]) return;
            const updated = normalizeAttachmentFile({
                ...attachments[index],
                URL: data.url,
                url: data.url,
                Name: data.file_name || attachments[index].Name,
                file_name: data.file_name || attachments[index].file_name,
                Size: data.size ?? attachments[index].Size,
                size: data.size ?? attachments[index].size,
                format: data.format || attachments[index].format,
                Extension: data.format || attachments[index].Extension,
            });
            this.$set(attachments, index, updated);
        },
        // mapAttachmentFromMediaServiceToPostStruct maps the uploaded file data from media service to the post attachment structure
        mapAttachmentFromMediaServiceToPostStruct(fileData: any) {
            return {
                URL: fileData?.url || '',
                Name: fileData?.file_name || '',
                Size: fileData?.size || 0,
                Extension: fileData?.format || '',
                CreatedAt: Math.floor(Date.now() / 1000),
            };
        },

        searchPages(query: string, source: any) {
            return new Promise((resolve, reject) => {
                // exit if query is less than 2 characters
                if (!query || query.length < 2) {
                    resolve([]);
                    return;
                }

                const filters = {
                    Query: query,
                    Limit: 20,
                };
                // Some deployments expose `/project/filter`, others `/page/filter` with different casing.
                const endpoints = ['/page/filter'];

                const tryEndpoint = (index: number) => {
                    if (index >= endpoints.length) {
                        reject(new Error('No page search endpoint responded'));
                        return;
                    }
                    axios({
                        method: 'POST',
                        url: endpoints[index],
                        withCredentials: true,
                        data: filters,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        cancelToken: source || null,
                    })
                        .then((response) => {
                            const objects =
                                get(response, 'data.Objects', null) ??
                                get(response, 'data.objects', null) ??
                                get(response, 'data.Projects', null) ??
                                get(response, 'data.projects', null) ??
                                [];
                            const output = (objects || []).map((page: any) =>
                                mapPageToSelectItem(page, this.$t ? this.$t.bind(this) : undefined)
                            );
                            resolve(output);
                        })
                        .catch((error) => {
                            if (error?.response?.status === 404) {
                                tryEndpoint(index + 1);
                            } else if (axios.isCancel && axios.isCancel(error)) {
                                reject(error);
                            } else {
                                console.error('Error fetching pages:', error);
                                reject(error);
                            }
                        });
                };

                tryEndpoint(0);
            });
        },

        resetDestinationSelection() {
            this.initializeDestinationState(this.runtimePrefilledContent as PrefilledContent);
        },
        isFeedItem(item: any) {
            return item && item.Value === this.feedOption.Value;
        },
        applyDestinationSelection({
            page = null,
            event = null,
            feed = null,
        }: {
            page?: PageOption | null;
            event?: any;
            feed?: boolean | null;
        } = {}) {
            const resolvedPage = this.destinationLocked && this.lockedPageItem ? this.lockedPageItem : page;
            const resolvedEvent =
                this.destinationLocked && this.destinationConfig.event ? this.destinationConfig.event : event;

            if (resolvedPage) {
                this.addPageOptionIfMissing(resolvedPage);
            }

            const feedAllowed = this.isFeedAllowedForPage(resolvedPage);
            const computedFeed =
                feed !== null && feed !== undefined
                    ? feed
                    : getInitialFeedSelection(this.destinationConfig.feedDisplay, !!(resolvedPage || resolvedEvent));

            let resolvedFeed = computedFeed;
            if (!resolvedFeed && !resolvedPage && !resolvedEvent) {
                resolvedFeed = true;
            }

            this.destinationSelection = {
                page: resolvedPage,
                event: resolvedEvent,
                feed: resolvedFeed,
            };

            this.feedOption.Disabled =
                this.feedLocked || (this.shouldUseImplicitDestinationRules && !!resolvedPage && !feedAllowed);
        },

        // handleDestinationSelect
        // - Shared selection handler for both consumer-defined destinations and search-driven destinations.
        // - Locks only freeze currently selected consumer-provided parts; they do not force feed on.
        handleDestinationSelect(selection: any[]) {
            const selectedPage =
                (selection || []).find((item) => !this.isFeedItem(item) && !this.isEventItem(item)) || null;
            const selectedEvent = (selection || []).find((item) => this.isEventItem(item)) || null;
            const feedSelectedRaw = (selection || []).some((item) => this.isFeedItem(item));
            const lockedPage = this.destinationLocked ? this.lockedPageItem : null;
            const lockedEvent = this.destinationLocked ? this.destinationConfig.event : null;
            const page = lockedPage || selectedPage;
            const event = lockedEvent || selectedEvent;
            const feedAllowed = this.isFeedAllowedForPage(page);
            const nothingSelected = !page && !event;
            const feed = this.feedLocked
                ? this.isFeedSelected
                : feedAllowed
                ? feedSelectedRaw || nothingSelected
                : false;
            this.applyDestinationSelection({ page, event, feed });
        },
        dedupeOptions(items: any[] = []) {
            const map = new Map();
            items.forEach((item) => {
                if (!item) return;
                const key =
                    item.Value !== undefined && item.Value !== null
                        ? String(item.Value)
                        : item.Name || Math.random().toString(36).slice(2);
                if (!map.has(key)) {
                    map.set(key, item);
                } else {
                    map.set(key, { ...map.get(key), ...item });
                }
            });
            return Array.from(map.values());
        },

        selectedPageForRedirect() {
            const isSharingFeaturedContentInFeed =
                !this.runtimeEditMode &&
                this.isFeedSelected &&
                get(this.runtimePrefilledContent, 'FeatureContentID', null) !== null &&
                !this.destinationLocked;
            // Feed shares return to the community feed, matching the event detail share flow.
            if (isSharingFeaturedContentInFeed) return null;
            return this.selectedPage || null;
        },
        redirectToPageIfNeeded(targetPage?: any, postId?: string | number | null) {
            const page = targetPage || this.selectedPageForRedirect();
            // if no target page, redirect / feed
            if (!page) {
                this.$router.push(postId ? `/?postpin=${postId}` : '/').catch(() => {});
                return;
            }
            const slug = get(page, 'Slug', null);
            if (!page || !slug || !this.$router || !this.$route) return;
            const currentSlug = get(this.$route, 'params.slug', null);
            const pinParam = postId ? `?postpin=${postId}` : '';
            const query = pinParam || '';
            const target = `/page/${slug}${query}`;
            this.$router.push(target).catch(() => {});
        },
        async getMyProjectsForSelector() {
            if (this.disablePageSearch) {
                this.pageOptions = [];
                return;
            }
            try {
                const response = await this.$store.dispatch('getMyProjects');
                const items =
                    (get(response, 'objects', []) || []).map((entry: any) =>
                        mapPageToSelectItem(get(entry, 'object', entry), this.$t ? this.$t.bind(this) : undefined)
                    ) || [];
                this.pageOptions = this.dedupeOptions([...(this.pageOptions || []), ...items]);
            } catch (error) {
                console.error('Error fetching my projects:', error);
            }
        },
        isFeedAllowedForPage(pageItem: PageOption | null) {
            if (!this.shouldUseImplicitDestinationRules || !pageItem) {
                return true;
            }
            return !isPagePrivateOrUnpublished(pageItem);
        },

        // resolveParentPayload
        // - Both entry modes resolve through the same selected page/event/feed state.
        // - Search-driven mode applies private/unpublished page rules; consumer-defined mode does not reinterpret them.
        resolveParentPayload() {
            const selectedPage = this.selectedPage;
            const selectedEvent = this.selectedEvent;
            const feedSelected = this.isFeedSelected;
            if (selectedEvent) {
                return {
                    ParentID: selectedEvent.EventID,
                    ParentType: 1,
                    FeedDisplay: getFeedDisplayForSelection(selectedEvent, feedSelected, false),
                };
            }
            if (selectedPage) {
                return {
                    ParentID: selectedPage.Value,
                    ParentType: 2,
                    FeedDisplay: getFeedDisplayForSelection(
                        selectedPage,
                        feedSelected,
                        this.shouldUseImplicitDestinationRules
                    ),
                };
            }
            if (feedSelected) {
                return {
                    ParentID: null,
                    ParentType: null,
                    FeedDisplay: 2,
                };
            }

            return {
                ParentID: null,
                ParentType: null,
                FeedDisplay: null,
            };
        },
        initializeDestinationState(
            prefilledContent: PrefilledContent = this.runtimePrefilledContent as PrefilledContent
        ) {
            const config = getDestinationConfig(prefilledContent, this.$t ? this.$t.bind(this) : undefined);
            const page = config.page
                ? {
                      ...config.page,
                      Disabled: !!config.lockParentDestination,
                  }
                : null;
            const event = config.event
                ? {
                      ...config.event,
                      Disabled: !!config.lockParentDestination,
                  }
                : null;

            this.destinationLocked = !!config.lockParentDestination;
            this.feedLocked = !!config.lockFeedDestination;
            this.lockedPageItem = this.destinationLocked ? page : null;

            if (this.disablePageSearch) {
                this.pageOptions = page ? [page] : [];
            }

            if (page) {
                this.addPageOptionIfMissing(page);
            }

            if (!config.hasDefinedDestination) {
                this.applyDestinationSelection({ page: null, event: null, feed: true });
                return;
            }

            this.applyDestinationSelection({
                page,
                event,
                feed: getInitialFeedSelection(config.feedDisplay, !!(page || event)),
            });
        },
        addPageOptionIfMissing(option: any) {
            if (!option) return;
            this.pageOptions = this.dedupeOptions([...(this.pageOptions || []), option]);
        },
        getPageName(page: any) {
            return get(page, 'Name', get(page, 'Title', this.$t ? this.$t('labels.page') : 'Page'));
        },
        onBeforeUnload() {
            if (!this.shouldWarnBeforeUnload()) return;
            this.persistDraft();
        },
        shouldWarnBeforeUnload(): boolean {
            return this.modalVisible && this.hasDraftableContent && !this.loading;
        },
        initializeDraftKey(
            prefilledContent: PrefilledContent | null = this.runtimePrefilledContent,
            editMode = this.runtimeEditMode
        ) {
            this.activeDraftKey = this.buildDraftStorageKey(prefilledContent, editMode);
        },
        buildDraftStorageKey(prefilledContent: PrefilledContent | null, editMode: boolean): string {
            const userId = get(this.user, 'ID', 'anonymous');
            const parentType = get(prefilledContent, 'ParentType', this.parentType || 'none');
            const parentId = get(prefilledContent, 'ParentID', get(this.parent, 'ID', 'none'));
            const postId = get(prefilledContent, 'ID', 'new');
            const routeScope = get(this, '$route.name', get(this, '$route.path', 'unknown-route'));
            const mode = editMode ? 'edit' : 'create';
            const isFeedDraftContext =
                !editMode &&
                !get(prefilledContent, 'ID', null) &&
                (parentType === null ||
                    parentType === undefined ||
                    parentType === '' ||
                    parentType === 'none' ||
                    parentType === 'null') &&
                (parentId === null ||
                    parentId === undefined ||
                    parentId === '' ||
                    parentId === 'none' ||
                    parentId === 'null');
            // Unify "new feed post" draft key across entry points (/feed and create menu).
            const scope = isFeedDraftContext ? 'feed-new-post' : routeScope;
            return [
                POST_EDITOR_DRAFT_KEY_PREFIX,
                String(userId),
                String(scope),
                String(parentType),
                String(parentId),
                String(mode),
                String(postId),
            ].join(':');
        },
        sanitizeContentForDraft(content: PostContent): PostContent {
            return {
                Text: content?.Text || '',
                Html: content?.Html || '',
                IsPoll: !!content?.IsPoll,
                Poll: content?.Poll || null,
                Attachments: Array.isArray(content?.Attachments)
                    ? content.Attachments.map((attachment: any) => ({
                          URL: attachment?.URL || attachment?.url || '',
                          url: attachment?.url || attachment?.URL || '',
                          Name: attachment?.Name || attachment?.file_name || '',
                          file_name: attachment?.file_name || attachment?.Name || '',
                          Size: attachment?.Size ?? attachment?.size ?? 0,
                          size: attachment?.size ?? attachment?.Size ?? 0,
                          format: attachment?.format || attachment?.Extension || '',
                          Extension: attachment?.Extension || attachment?.format || '',
                          CreatedAt: attachment?.CreatedAt || Math.floor(Date.now() / 1000),
                      }))
                    : [],
            };
        },
        persistDraft() {
            if (!this.modalVisible || !this.activeDraftKey || this.loading) return;
            if (!this.hasDraftableContent) {
                // Keep storage clean: remove stale draft when editor has no meaningful content.
                this.clearDraft();
                return;
            }
            try {
                const payload = {
                    version: 1,
                    timestamp: Date.now(),
                    content: this.sanitizeContentForDraft(this.content || {}),
                    destination: this.sanitizeDestinationForDraft(),
                };
                this.$store.commit('SET_POST_EDITOR_DRAFT', {
                    key: this.activeDraftKey,
                    draft: payload,
                });
            } catch (error) {
                console.error('Error persisting post editor draft:', error);
            }
        },
        hydrateDraftFromStorage() {
            if (!this.activeDraftKey || this.hasContent) return;
            try {
                const parsed = get(this.$store.state, `postEditorDrafts.${this.activeDraftKey}`, null);
                if (!parsed) return;
                const timestamp = Number(parsed?.timestamp || 0);
                if (!timestamp || Date.now() - timestamp > POST_EDITOR_DRAFT_TTL_MS) {
                    this.clearDraft();
                    return;
                }
                const restoredContent = this.sanitizeContentForDraft(parsed?.content || {});
                const hasRestorableContent =
                    !!restoredContent.Html ||
                    !!restoredContent.Text ||
                    !!restoredContent.IsPoll ||
                    (restoredContent.Attachments && restoredContent.Attachments.length > 0);
                if (!hasRestorableContent) return;
                this.restoredDraftContent = restoredContent;
                this.content = {
                    ...(this.content || {}),
                    ...restoredContent,
                };
                if (!this.destinationConfig.hasDefinedDestination) {
                    this.restoreDestinationFromDraft(parsed?.destination || null);
                }
                this.notifyDraftRestored();
            } catch (error) {
                console.error('Error restoring post editor draft:', error);
            }
        },
        clearDraft() {
            if (!this.activeDraftKey) return;
            try {
                this.$store.commit('CLEAR_POST_EDITOR_DRAFT', this.activeDraftKey);
            } catch (error) {
                console.error('Error clearing post editor draft:', error);
            }
        },
        isValidPoll(poll: any): boolean {
            if (!poll) return false;
            const question = get(poll, 'PollQuestion', '').trim();
            if (!question) return false;
            const options = (get(poll, 'PollOptions', []) || []).filter((option: any) => {
                const text = get(option, 'Text', '');
                return !!text && text.trim().length > 0;
            });
            return options.length >= 2;
        },
        hasAnyPollDraft(poll: any): boolean {
            if (!poll) return false;
            const question = get(poll, 'PollQuestion', '').trim();
            if (question.length > 0) return true;
            const options = get(poll, 'PollOptions', []) || [];
            return options.some((option: any) => {
                const text = get(option, 'Text', '');
                return !!text && text.trim().length > 0;
            });
        },
        hasDraftableTextContent(): boolean {
            const text = get(this.content, 'Text', '') || '';
            if (text.trim().length > 0) return true;
            const html = get(this.content, 'Html', '') || '';
            if (!html || html.trim().length < 1) return false;
            const htmlAsText = html
                .replace(/<br\s*\/?>/gi, '')
                .replace(/&nbsp;/gi, ' ')
                .replace(/<[^>]*>/g, '')
                .trim();
            return htmlAsText.length > 0;
        },
        sanitizeDestinationForDraft() {
            return {
                page: this.selectedPage ? { ...this.selectedPage } : null,
                event: this.selectedEvent ? { ...this.selectedEvent } : null,
                feed: !!this.isFeedSelected,
            };
        },
        restoreDestinationFromDraft(destination: any) {
            if (!destination) return;
            const page = destination?.page || null;
            const event = destination?.event || null;
            const feed = destination?.feed;

            // Ensure restored page destination is selectable again after reopening the editor.
            if (page) {
                this.addPageOptionIfMissing(page);
            }

            this.applyDestinationSelection({
                page,
                event: this.isEventSelectorVisible ? event : null,
                feed: typeof feed === 'boolean' ? feed : null,
            });
        },
        notifyDraftSaved() {
            EventBus.$emit('INFO', {
                Message: this.$t('messages.postDraftSaved'),
                Details: '',
            });
        },
        notifyDraftRestored() {
            EventBus.$emit('INFO', {
                Message: this.$t('messages.postDraftRestored'),
                Details: '',
            });
        },
    },
};
